import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowLeft, Save, Share2, Check } from 'lucide-react';
import { post } from 'aws-amplify/api';
import { fetchAuthSession, fetchUserAttributes } from 'aws-amplify/auth';
import { v4 as uuidv4 } from 'uuid';
import AuthButton from './AuthButton';

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";

export function Header({
    companyName,
    ticker,
    isAnalyzing,
    onSave,
    analysis,
    yearlyGrowthRates,
    sensitivityParams,
    setError
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [showAuthDialog, setShowAuthDialog] = useState(false);
    const [authAction, setAuthAction] = useState('');
    const [isAuthenticating, setIsAuthenticating] = useState(false);
    const [isCopied, setIsCopied] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userEmail, setUserEmail] = useState(null);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                const session = await fetchAuthSession();
                const userInfo = session.tokens?.idToken?.payload;
                setIsAuthenticated(!!userInfo);
                if (userInfo) {
                    setUserEmail(userInfo.email || userInfo['cognito:username']);
                }
            } catch (error) {
                console.error('Error checking auth status:', error);
                setIsAuthenticated(false);
            }
        };

        checkAuth();
    }, []);

    // Check if we're in the ValueInvestingDashboard
    const isValueInvestingDashboard = location.pathname === '/';

    // Extract analysis ID from URL if present
    const analysisId = location.pathname.match(/\/analysis\/([^\/]+)/)?.[1];

    // Determine if we should show the share button
    const shouldShowShareButton = !isValueInvestingDashboard && analysisId;

    const handleBack = () => {
        navigate('/');
    };

    const handleAuthSuccess = async () => {
        setShowAuthDialog(false);
        setIsAuthenticating(false);
        // Recheck authentication after successful login
        const session = await fetchAuthSession();
        const userInfo = session.tokens?.idToken?.payload;
        setIsAuthenticated(!!userInfo);
        setUserEmail(userInfo?.email || userInfo?.['cognito:username']);

        if (authAction === 'save') {
            await handleSave();
        }
    };

    const handleShare = async () => {
        // Create the full URL for sharing
        const shareUrl = `${window.location.origin}/analysis/${analysisId}`;

        try {
            await navigator.clipboard.writeText(shareUrl);
            setIsCopied(true);

            // Reset the copied state after 2 seconds
            setTimeout(() => {
                setIsCopied(false);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
            if (typeof setError === 'function') {
                setError('Failed to copy link. Please try again.');
            }
        }
    };

    const handleSave = async () => {
        if (!isAuthenticated) {
            setAuthAction('save');
            setShowAuthDialog(true);
            return;
        }

        if (!analysis || !analysis.fcfAnalysis || !analysis.companyInfo || !analysis.valuation) {
            console.error("Analysis data is incomplete or undefined");
            if (typeof setError === 'function') {
                setError('Unable to save: Analysis data is incomplete');
            }
            return;
        }

        try {
            const frontendAnalysisId = uuidv4();
            const userAttributes = await fetchUserAttributes();
            const username = userAttributes.preferred_username;

            // Create a deep copy of the FCF analysis array
            const updatedFcfAnalysis = analysis.fcfAnalysis.map((yearData, index) => {
                // Calculate the projected FCF based on the growth rate
                const baseFcf = analysis.fcfAnalysis[0].fcf;
                let projectedFcf = baseFcf;

                // Apply cumulative growth rates up to this year
                for (let i = 0; i < index; i++) {
                    projectedFcf *= (1 + (yearlyGrowthRates[i] / 100));
                }

                return {
                    ...yearData,
                    fcf: projectedFcf,
                    growth_rate: yearlyGrowthRates[index] || yearData.growth_rate
                };
            });

            const analysisData = {
                id: frontendAnalysisId,
                ticker: analysis.companyInfo.ticker,
                createdBy: userEmail,
                createdAt: new Date().toISOString(),
                analysis: {
                    createdBy: username,
                    company_info: analysis.companyInfo,
                    fcf_analysis: updatedFcfAnalysis,
                    analyst_thesis: analysis.analystThesis,
                    valuation: {
                        ...analysis.valuation,
                        wacc: sensitivityParams?.wacc || analysis.valuation.wacc,
                        terminal_growth: sensitivityParams?.terminalGrowth || analysis.valuation.terminalGrowth,
                        growthRate: yearlyGrowthRates[0] // Use first year's growth rate as the base rate
                    }
                }
            };

            const session = await fetchAuthSession();
            const token = session.tokens.idToken.toString();

            const response = await post({
                apiName: 'dcf',
                path: `/analysis/${frontendAnalysisId}`,
                options: {
                    body: analysisData,
                    headers: {
                        'Authorization': token,
                        'Content-Type': 'application/json'
                    }
                },
            }).response;

            const reader = response.body.getReader();
            const { value } = await reader.read();
            const text = new TextDecoder().decode(value);
            const result = JSON.parse(text);

            console.log("Save successful:", result);

            if (result.id) {
                navigate(`/analysis/${result.id}`);
            } else {
                console.error("No analysis ID returned from the server");
                setError('Failed to save analysis: No ID returned');
            }

        } catch (error) {
            console.error('Error saving analysis:', error);
            if (typeof setError === 'function') {
                setError('Failed to save analysis. Please try again.');
            }
        }
    };

    return (
        <>
            <div className="sticky top-0 z-50 bg-white border-b shadow-sm">
                <div className="max-w-7xl mx-auto">
                    <div className="flex justify-between items-center h-16 px-4">
                        <div className="flex items-center">
                            <Button variant="ghost" size="icon" className="mr-4" onClick={handleBack}>
                                <ArrowLeft className="h-4 w-4" />
                            </Button>
                            <h1 className="text-xl font-semibold">
                                {isAnalyzing ? 'Analyzing...' :
                                    companyName ? `${companyName} (${ticker}) - DCF Analysis` :
                                        'New DCF Analysis'}
                            </h1>
                        </div>
                        <div className="flex items-center space-x-3">
                            {onSave && (
                                <Button onClick={handleSave}>
                                    <Save className="h-4 w-4 mr-2" />
                                    Save Analysis
                                </Button>
                            )}
                            {shouldShowShareButton && (
                                <Button
                                    variant="outline"
                                    onClick={handleShare}
                                    className="min-w-[120px]"
                                >
                                    {isCopied ? (
                                        <>
                                            <Check className="h-4 w-4 mr-2" />
                                            Copied!
                                        </>
                                    ) : (
                                        <>
                                            <Share2 className="h-4 w-4 mr-2" />
                                            Share
                                        </>
                                    )}
                                </Button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Only show auth dialog if user is not authenticated */}
            {!isAuthenticated && (
                <Dialog open={showAuthDialog} onOpenChange={setShowAuthDialog}>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader className="text-center">
                            <DialogTitle className="text-xl">Sign In Required</DialogTitle>
                            <div className="text-sm text-gray-500 mt-2">
                                Please sign in or create an account to save your analysis.
                            </div>
                        </DialogHeader>
                        <div className="flex flex-col items-center space-y-4 py-4">
                            <div className="w-full">
                                <AuthButton
                                    onAuthSuccess={handleAuthSuccess}
                                    setIsAuthenticating={setIsAuthenticating}
                                    className="w-full"
                                />
                            </div>
                            <div className="w-full">
                                <Button
                                    variant="outline"
                                    onClick={() => setShowAuthDialog(false)}
                                    className="w-full"
                                >
                                    Cancel
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            )}
        </>
    );
}

export default Header;